
import { useState } from 'react';

export default function DecisionCriteria() {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [checklistAnswers, setChecklistAnswers] = useState<{[key: string]: string}>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleConsultationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d3c8ocsef3sotc9j5t80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        alert('お問い合わせありがとうございます。担当者より3営業日以内にご連絡いたします。');
        form.reset();
        setShowConsultationForm(false);
      }
    } catch (error) {
      alert('送信に失敗しました。お電話でのお問い合わせもお受けしております。');
    }
  };

  const handleChecklistChange = (key: string, value: string) => {
    setChecklistAnswers(prev => ({ ...prev, [key]: value }));
  };

  const criteriaData = [
    {
      id: 'shape',
      title: '積載物の形状',
      icon: 'ri-box-3-line',
      description: '段ボール・袋・プラケースなど製品形状により最適なハンドが決まります',
      details: [
        {
          type: '段ボール箱',
          recommendation: '挟み込み型ハンド',
          characteristics: '側面を挟んで持ち上げる方式。安定性が高く荷崩れしにくい'
        },
        {
          type: '袋物・フィルム包装',
          recommendation: '吸着型ハンド',
          characteristics: '真空パッドで吸着。柔軟な形状にも対応可能'
        },
        {
          type: 'プラケース',
          recommendation: '挟み込み型・組み合わせ型',
          characteristics: '材質と形状に応じて最適なハンドを選定'
        }
      ],
      checklistQuestion: '主な積載物の形状は？',
      checklistOptions: ['段ボール箱', '袋物', 'プラケース', '複数種類']
    },
    {
      id: 'weight',
      title: '可搬質量',
      icon: 'ri-scales-3-line',
      description: 'ハンド重量込みで計算。安全率を考慮した選定が重要',
      details: [
        {
          type: '軽量物（10kg以下）',
          recommendation: '直交ロボット',
          characteristics: '省スペース・低コストで対応可能'
        },
        {
          type: '中重量物（10-30kg）',
          recommendation: '多関節ロボット',
          characteristics: '柔軟性と処理能力のバランスが良好'
        },
        {
          type: '重量物（30kg以上）',
          recommendation: '機械式・大型多関節',
          characteristics: '高負荷に対応できる堅牢な構造'
        }
      ],
      checklistQuestion: '1個あたりの重量は？',
      checklistOptions: ['10kg以下', '10-30kg', '30kg以上', '不明']
    },
    {
      id: 'capacity',
      title: '処理能力',
      icon: 'ri-speed-up-line',
      description: 'cycle/h表記で評価。複数つかみ対応で能力向上可能',
      details: [
        {
          type: '低処理能力（200-400ケース/時）',
          recommendation: '直交ロボット',
          characteristics: '中小企業向け。コスト重視の導入に最適'
        },
        {
          type: '中処理能力（400-600ケース/時）',
          recommendation: '多関節ロボット',
          characteristics: '多品種対応と処理能力を両立'
        },
        {
          type: '高処理能力（800-1200ケース/時）',
          recommendation: '機械式',
          characteristics: '大量生産ラインに最適。飲料業界で実績多数'
        }
      ],
      checklistQuestion: '必要な処理能力は？',
      checklistOptions: ['400ケース/時以下', '400-800ケース/時', '800ケース/時以上', '不明']
    },
    {
      id: 'space',
      title: '設置スペース',
      icon: 'ri-building-line',
      description: '安全柵・動線・搬入経路を含めた総合的な検討が必要',
      details: [
        {
          type: '狭小スペース（30-50㎡）',
          recommendation: '直交ロボット',
          characteristics: '省スペース設計。天井高の制約にも対応'
        },
        {
          type: '中規模スペース（50-80㎡）',
          recommendation: '多関節ロボット',
          characteristics: '動作範囲が広く、複数パレット対応可能'
        },
        {
          type: '大規模スペース（100㎡以上）',
          recommendation: '機械式',
          characteristics: '高処理能力を発揮。専用レイアウト設計'
        }
      ],
      checklistQuestion: '利用可能な設置スペースは？',
      checklistOptions: ['50㎡以下', '50-100㎡', '100㎡以上', '未確認']
    },
    {
      id: 'support',
      title: 'サポート・実績',
      icon: 'ri-customer-service-2-line',
      description: 'IoT監視・遠隔対応など充実したサポート体制',
      details: [
        {
          type: '24時間サポート',
          recommendation: '必須要件',
          characteristics: '生産ライン停止リスクを最小化'
        },
        {
          type: 'IoT監視システム',
          recommendation: '予防保全',
          characteristics: '故障予知とメンテナンス最適化'
        },
        {
          type: '導入実績',
          recommendation: '同業界実績重視',
          characteristics: '類似環境での成功事例を参考に選定'
        }
      ],
      checklistQuestion: 'サポート体制の重要度は？',
      checklistOptions: ['非常に重要', '重要', 'やや重要', '重要でない']
    },
    {
      id: 'cost',
      title: '費用対効果',
      icon: 'ri-money-dollar-circle-line',
      description: '投資回収期間を明確にした導入判断',
      details: [
        {
          type: '短期回収（2年以内）',
          recommendation: '高効果領域',
          characteristics: '人件費削減効果が大きい案件'
        },
        {
          type: '中期回収（2-4年）',
          recommendation: '標準的導入',
          characteristics: 'バランスの取れた投資効果'
        },
        {
          type: '長期回収（4年以上）',
          recommendation: '戦略的投資',
          characteristics: '省人化以外の付加価値も考慮'
        }
      ],
      checklistQuestion: '希望投資回収期間は？',
      checklistOptions: ['2年以内', '2-4年', '4年以上', '未検討']
    }
  ];

  const getScoreColor = (score: number) => {
    if (score >= 18) return 'text-green-600';
    if (score >= 12) return 'text-yellow-600';
    return 'text-red-600';
  };

  const calculateScore = () => {
    const values = Object.values(checklistAnswers);
    const weights = { '0': 1, '1': 2, '2': 3, '3': 0 }; // 0: 不明/未検討, 1-3: 選択肢の重み
    return values.reduce((sum, value) => {
      const index = parseInt(value) || 0;
      return sum + (weights[index as keyof typeof weights] || 0);
    }, 0);
  };

  const getRecommendation = (score: number) => {
    if (score >= 18) return {
      level: '導入推奨',
      message: 'パレタイザー導入による効果が期待できます',
      color: 'green'
    };
    if (score >= 12) return {
      level: '検討推奨',
      message: '詳細検討により最適解を見つけられます',
      color: 'yellow'
    };
    return {
      level: '要検討',
      message: '現状分析から始めることをお勧めします',
      color: 'red'
    };
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-800 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <a href="/" className="text-2xl font-bold hover:text-orange-400 cursor-pointer" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</a>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
            <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
            <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
            <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
            <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
            <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
            <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
          </nav>

          <div className="flex items-center space-x-6">
            <button 
              onClick={() => setShowConsultationForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              見積依頼・お問い合わせ
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white cursor-pointer"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-700">
            <nav className="flex flex-col space-y-4 mt-4">
              <a href="/palletizer-basics" className="hover:text-orange-400 transition-colors cursor-pointer">パレタイザーとは</a>
              <a href="/selection-guide" className="hover:text-orange-400 transition-colors cursor-pointer">機種選定ガイド</a>
              <a href="/decision-criteria" className="hover:text-orange-400 transition-colors cursor-pointer">導入判断基準</a>
              <a href="/case-studies" className="hover:text-orange-400 transition-colors cursor-pointer">導入事例</a>
              <a href="/manufacturers-products" className="hover:text-orange-400 transition-colors cursor-pointer">メーカー・製品</a>
              <a href="/peripheral-systems" className="hover:text-orange-400 transition-colors cursor-pointer">周辺機器</a>
              <a href="/alternatives-comparison" className="hover:text-orange-400 transition-colors cursor-pointer">代替案・比較</a>
              <button 
                onClick={() => {
                  setShowConsultationForm(true);
                  setMobileMenuOpen(false);
                }}
                className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors text-left"
              >
                見積依頼・お問い合わせ
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* ... existing code ... */}
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4 px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center space-x-2 text-sm text-slate-600">
            <a href="/" className="hover:text-blue-600 cursor-pointer">ホーム</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-800 font-semibold">導入判断基準</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">導入判断の6つの基準</h1>
          <p className="text-xl text-purple-100 mb-8">失敗しない選び方で最適なパレタイザーを選定</p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-4xl font-bold text-slate-800 mb-6">導入前のお悩みごと</h2>
              <div className="space-y-4 mb-8">
                {[
                  'パレタイザーで本当に省人化・自動化が実現できるのかな...',
                  '自社に合っているパレタイザーはどの種類かな...',
                  '判断基準が分からず、パレタイザーの導入を躊躇している...'
                ].map((concern, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-4 mt-1">
                      <i className="ri-question-line text-red-600 text-sm"></i>
                    </div>
                    <p className="text-slate-600">{concern}</p>
                  </div>
                ))}
              </div>
              <p className="text-lg text-slate-700">
                これら6つの判断基準を事前に認識しておくことで、パレタイザーの導入可否や
                自社に適したパレタイザー選定の検討をスムーズに進めることができます。
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Business%20meeting%20discussing%20industrial%20automation%20decisions%2C%20professionals%20analyzing%20charts%20and%20factory%20layouts%2C%20modern%20conference%20room%20with%20industrial%20equipment%20catalogs%2C%20decision-making%20process%20visualization&width=600&height=400&seq=decision-criteria-intro&orientation=landscape"
                alt="導入判断基準"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* 6 Criteria Overview */}
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {criteriaData.map((criteria, index) => (
              <div key={index} className="bg-purple-50 p-6 rounded-lg text-center border-2 border-purple-200">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className={`${criteria.icon} text-2xl text-purple-600`}></i>
                </div>
                <h3 className="font-bold text-slate-800 text-sm">{criteria.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Criteria Sections */}
      {criteriaData.map((criteria, index) => (
        <section key={index} className={`py-20 ${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mr-6">
                <i className={`${criteria.icon} text-3xl text-purple-600`}></i>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-slate-800 mb-2">
                  {index + 1}. {criteria.title}
                </h2>
                <p className="text-lg text-slate-600">{criteria.description}</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {criteria.details.map((detail, detailIndex) => (
                <div key={detailIndex} className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
                  <h3 className="text-xl font-bold text-slate-800 mb-3">{detail.type}</h3>
                  <div className="bg-purple-50 p-3 rounded-lg mb-4">
                    <span className="text-sm font-semibold text-purple-800">推奨: </span>
                    <span className="text-sm text-purple-600">{detail.recommendation}</span>
                  </div>
                  <p className="text-slate-600">{detail.characteristics}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Self-Assessment Checklist */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">自社適合度チェックリスト</h2>
          <div className="bg-white text-slate-800 p-8 rounded-lg">
            <div className="space-y-6">
              {criteriaData.map((criteria, index) => (
                <div key={index} className="border-b border-slate-200 pb-6 last:border-b-0">
                  <h3 className="text-lg font-bold mb-4">{criteria.checklistQuestion}</h3>
                  <div className="grid md:grid-cols-2 gap-2">
                    {criteria.checklistOptions.map((option, optionIndex) => (
                      <label key={optionIndex} className="flex items-center cursor-pointer">
                        <input 
                          type="radio" 
                          name={criteria.id}
                          value={optionIndex.toString()}
                          onChange={(e) => handleChecklistChange(criteria.id, e.target.value)}
                          className="mr-3"
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {Object.keys(checklistAnswers).length === criteriaData.length && (
              <div className="mt-8 p-6 bg-slate-50 rounded-lg text-center">
                <div className="text-3xl font-bold mb-4">
                  <span className="text-slate-600">適合度スコア: </span>
                  <span className={getScoreColor(calculateScore())}>{calculateScore()}/18</span>
                </div>
                <div className={`text-xl font-bold mb-4 ${getRecommendation(calculateScore()).color === 'green' ? 'text-green-600' : getRecommendation(calculateScore()).color === 'yellow' ? 'text-yellow-600' : 'text-red-600'}`}>
                  {getRecommendation(calculateScore()).level}
                </div>
                <p className="text-slate-600 mb-6">{getRecommendation(calculateScore()).message}</p>
                <button 
                  onClick={() => setShowConsultationForm(true)}
                  className="bg-orange-500 hover:bg-orange-600 px-8 py-3 rounded-lg font-bold text-white whitespace-nowrap cursor-pointer transition-colors"
                >
                  詳細相談で最適プランを提案
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-8">導入判断でお悩みですか？</h2>
          <p className="text-xl mb-8">6つの基準を詳しく分析し、最適なパレタイザーをご提案いたします</p>
          <div className="flex justify-center space-x-6">
            <a 
              href="/selection-guide"
              className="bg-white text-purple-600 hover:bg-slate-100 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              機種選定ガイドを見る
            </a>
            <button 
              onClick={() => setShowConsultationForm(true)}
              className="bg-orange-500 hover:bg-orange-600 px-10 py-4 rounded-lg text-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
            >
              専門家に相談する
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Arial", "Helvetica", sans-serif' }}>パレタイザーナビ</h3>
              <p className="text-slate-300 mb-4">パレタイザー専門企業として、製造業・物流業の自動化をサポートします。</p>
              <div className="flex space-x-4">
                <i className="ri-phone-line text-orange-400"></i>
                <span>03-1234-5678</span>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">機械式パレタイザー</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">多関節ロボット</a></li>
                <li><a href="/selection-guide" className="hover:text-orange-400 cursor-pointer">直交ロボット</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">保守・メンテナンス</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">詳細情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li><a href="/palletizer-basics" className="hover:text-orange-400 cursor-pointer">パレタイザーとは</a></li>
                <li><a href="/decision-criteria" className="hover:text-orange-400 cursor-pointer">導入判断基準</a></li>
                <li><a href="/case-studies" className="hover:text-orange-400 cursor-pointer">導入事例</a></li>
                <li><a href="/manufacturers-products" className="hover:text-orange-400 cursor-pointer">メーカー・製品</a></li>
                <li><a href="/peripheral-systems" className="hover:text-orange-400 cursor-pointer">周辺機器・システム</a></li>
                <li><a href="/alternatives-comparison" className="hover:text-orange-400 cursor-pointer">代替案・比較</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">会社情報</h4>
              <ul className="space-y-2 text-slate-300">
                <li>〒174-0000 東京都板橋区</li>
                <li>設立：1985年</li>
                <li>従業員数：120名</li>
                <li>ISO9001認証取得</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 パレタイザーナビ. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-orange-400 hover:text-orange-300 ml-2">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-slate-800">お問い合わせ・見積もり</h3>
                <button 
                  onClick={() => setShowConsultationForm(false)}
                  className="text-slate-500 hover:text-slate-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form id="decision-criteria-form" data-readdy-form onSubmit={handleConsultationSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">会社名 *</label>
                    <input 
                      type="text" 
                      name="company" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">お名前 *</label>
                    <input 
                      type="text" 
                      name="name" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">電話番号 *</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">メールアドレス *</label>
                    <input 
                      type="email" 
                      name="email" 
                      required 
                      className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">現在の課題・検討状況</label>
                  <textarea 
                    name="current_situation" 
                    rows={4} 
                    maxLength={500}
                    className="w-full p-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="積載物の種類、処理量、設置環境などお聞かせください（500文字以内）"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">希望連絡方法</label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input type="radio" name="contact_method" value="電話" className="mr-2" />
                      電話
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="contact_method" value="メール" className="mr-2" />
                      メール
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="contact_method" value="どちらでも" className="mr-2" defaultChecked />
                      どちらでも
                    </label>
                  </div>
                </div>
                <button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-bold whitespace-nowrap cursor-pointer transition-colors"
                >
                  送信する
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
